import { employeeHomePath, translateOr, useTranslate } from "@/shared";
import { Button, TopBar } from "@/ui";
import { Link, useSearch } from "@tanstack/react-router";
import { MapPin, Plus } from "lucide-react";
import { InstallationPlansTable } from "../../components/InstallationPlansTable";
import { useInstallationPlansSearch } from "../../hooks/use-installation-plans-search";
import { useProjectById } from "../../hooks/use-project-by-id";
import type { ProjectDetailsRouteSearch } from "../../routes";
import { formatDate } from "../../utils/format-date";
import { resolveStateNames } from "../../utils/geography";
import { pmCreateInstallationPlanPath, pmCreateProjectPath, pmMyProjectsPath } from "../../utils/paths";
import { formatStatusLabel } from "../../utils/status-label";

export function ProjectDetailsPage() {
  const { t } = useTranslate();
  const search = useSearch({ strict: false }) as ProjectDetailsRouteSearch;
  const projectId = search.projectId;

  const { data: project } = useProjectById(projectId);
  const { data, isLoading } = useInstallationPlansSearch({ projectId });

  const geography = project?.additionalDetails?.geographyDetails;
  const districtCount = geography?.districts?.length ?? 0;
  const blockCount = geography?.blocks?.length ?? 0;

  return (
    <div className="w-full space-y-6">
      <TopBar
        title={project?.name ?? "-"}
        breadcrumbs={[
          { label: translateOr(t, "CORE_COMMON_OVERVIEW", "Overview"), to: employeeHomePath() },
          { label: translateOr(t, "ES_PM_MY_PROJECTS", "My Projects"), to: pmMyProjectsPath() },
          { label: project?.name ?? "-" },
        ]}
      />

      <div className="livelihood-card p-6">
        <div className="mb-6 flex items-center justify-between gap-4">
          <h2 className="text-lg font-semibold text-primary">
            {translateOr(t, "ES_PM_PROJECT_DETAILS", "Project Details")}
          </h2>
          {/* Reopens the project wizard on its geography step; places used by an installation
              plan stay locked there, and new blocks get their end-user sites on the next step. */}
          {projectId ? (
            <Button asChild variant="outline" size="sm">
              <Link to={pmCreateProjectPath()} search={{ projectId, step: 2 }}>
                <MapPin className="size-4" />
                {translateOr(t, "ES_PM_EDIT_GEOGRAPHY", "Edit Geography")}
              </Link>
            </Button>
          ) : null}
        </div>
        <div className="grid gap-6 sm:grid-cols-2">
          <div className="space-y-4">
            <div>
              <p className="text-xs font-medium text-muted-foreground">{translateOr(t, "ES_PM_STATE", "State")}</p>
              <p className="text-sm text-foreground">{resolveStateNames(geography, t).join(", ") || "-"}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">
                {translateOr(t, "ES_PM_DISTRICTS", "District(s)")}
              </p>
              <p className="text-sm font-medium text-primary">
                {t("ES_PM_N_SELECTED", { count: districtCount, defaultValue: "{{count}} selected" })}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">{translateOr(t, "ES_PM_BLOCKS", "Block(s)")}</p>
              <p className="text-sm font-medium text-primary">
                {t("ES_PM_N_SELECTED", { count: blockCount, defaultValue: "{{count}} selected" })}
              </p>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <p className="text-xs font-medium text-muted-foreground">
                {translateOr(t, "ES_PM_PROJECT_STATUS", "Project Status")}
              </p>
              <p className="text-sm text-foreground">{formatStatusLabel(t, project?.additionalDetails?.status)}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">
                {translateOr(t, "ES_PM_PROJECT_DATES", "Project Dates")}
              </p>
              <p className="text-sm text-foreground">
                {formatDate(project?.startDate)} - {formatDate(project?.endDate)}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-primary">
            {translateOr(t, "ES_PM_INSTALLATION_PLANS", "Installation Plans")}
          </h2>
          <Button asChild variant="outline" size="sm">
            <Link to={pmCreateInstallationPlanPath()} search={{ projectId }}>
              <Plus className="size-4" />
              {translateOr(t, "ES_PM_ADD_NEW", "Add New")}
            </Link>
          </Button>
        </div>
        <InstallationPlansTable plans={data?.plans ?? []} isLoading={isLoading} />
      </div>
    </div>
  );
}
